/**
 * InspectionAnalyticsTab — read-only dashboard with three sections:
 *   1. Overview cards (total, avg score, pending)
 *   2. Per-category pass-rate bars
 *   3. Top 5 most-failed checklist items
 *
 * The aggregate is computed once (in the parent) and passed in as `analytics`.
 * Lives in /components/inspections/ so the page component stays focused.
 */
import { Activity, TrendingUp, TrendingDown, Clock, BarChart3 } from 'lucide-react';

const tintForScore = (n) => (n >= 80 ? '#22c55e' : n >= 60 ? '#f59e0b' : '#ef4444');

export default function InspectionAnalyticsTab({ analytics }) {
  return (
    <div data-testid="inspection-analytics" style={{ marginBottom: '24px' }}>
      <div style={{ display: 'grid', gap: '20px' }}>
        {/* Overview Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
        }}>
          <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Activity size={20} color="#0073ea" />
              <span style={{ fontSize: '13px', color: '#6b7280' }}>Total Inspections</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937' }}>{analytics.totalInspections}</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>{analytics.last30DaysCount} in last 30 days</div>
          </div>

          <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <TrendingUp size={20} color="#22c55e" />
              <span style={{ fontSize: '13px', color: '#6b7280' }}>Average Score</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: tintForScore(analytics.avgScore) }}>
              {analytics.avgScore}%
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Based on {analytics.completedCount} completed</div>
          </div>

          <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Clock size={20} color="#f59e0b" />
              <span style={{ fontSize: '13px', color: '#6b7280' }}>Pending Review</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937' }}>
              {analytics.pendingCount + analytics.inProgressCount}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {analytics.pendingCount} pending, {analytics.inProgressCount} in progress
            </div>
          </div>
        </div>

        {/* Category Performance */}
        <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BarChart3 size={20} color="#0073ea" />
            Category Performance
          </h3>
          <div style={{ display: 'grid', gap: '12px' }}>
            {Object.entries(analytics.categoryStats).map(([key, stat]) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '120px', fontSize: '14px', color: '#374151' }}>{stat.label}</div>
                <div style={{ flex: 1, height: '24px', background: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${stat.passRate}%`,
                    background: tintForScore(stat.passRate),
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    paddingRight: '8px',
                    transition: 'width 0.3s',
                  }}>
                    {stat.passRate > 15 && (
                      <span style={{ fontSize: '12px', fontWeight: '600', color: 'white' }}>{stat.passRate}%</span>
                    )}
                  </div>
                </div>
                {stat.passRate <= 15 && (
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', width: '40px' }}>{stat.passRate}%</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Top Issues */}
        <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <TrendingDown size={20} color="#ef4444" />
            Common Issues (Top 5)
          </h3>
          {analytics.topIssues.length === 0 ? (
            <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>No issues recorded yet</p>
          ) : (
            <div style={{ display: 'grid', gap: '8px' }}>
              {analytics.topIssues.map((issue) => (
                <div
                  key={issue.type || issue.label || issue.name}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    background: '#fef2f2',
                    borderRadius: '8px',
                    borderLeft: '4px solid #ef4444',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: '500', color: '#1f2937' }}>{issue.name}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>{issue.category}</div>
                  </div>
                  <div style={{
                    background: '#fee2e2',
                    color: '#dc2626',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontWeight: '600',
                    fontSize: '14px',
                  }}>
                    {issue.count} fails
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
